<template>
    <div class="quiz-container">
        <Stepper
            :current-step="currentQuestion"
            :step-length="questionsCount"
            @next="next"
            @prev="prev"
            @restart="restart"
        >
            <template #step1>
                <Question
                    v-model="results[0].id"
                    :question="questions[0]"
                    :error-msg="errors['question1']"
                    :current-question="currentQuestion"
                />
            </template>
            <template #step2>
                <Question
                    v-if="additionnalQuestion"
                    v-model="results[1].id"
                    :question="questions[1]"
                    :error-msg="errors['question2']"
                    :current-question="currentQuestion"
                />
            </template>
            <template #[`step${additionnalQuestion?3:2}`]>
                <Question
                    v-model="results[2].id"
                    :question="questions[2]"
                    :error-msg="errors['question3']"
                    :current-question="additionnalQuestion ? currentQuestion : currentQuestion + 1"
                />
            </template>
            <template #[`step${additionnalQuestion?4:3}`]>
                <Question
                    v-if="results[2].id === 1"
                    v-model="results[3].id"
                    :question="questions[3].alternatives[0]"
                    :results="results"
                    :error-msg="errors['question4']"
                    :dynamic-error-msg="errors['nbRides']"
                    :current-question="3"
                    :label="t('specifyNb')"
                />
                <fieldset v-if="results[2].id === 2">
                    <Question :question="questions[3].alternatives[1]" :current-question="3">
                        <template #answers>
                            <div
                                v-for="(day, i) in questions[3].alternatives[1].days"
                                :key="i"
                                class="radio-list-wrapper"
                            >
                                <h3 class="day-label">{{ day }}</h3>
                                <ul class="no-bullet radio-list" role="radiogroup" :aria-describedby="`error${day}`">
                                    <li v-for="(answer, j) in questions[3].alternatives[1].answers" :key="j">
                                        <div class="radio-wrapper">
                                            <Field
                                                :id="`input-radio-${day}-${j}`"
                                                v-model="results[3].wkd[i].id"
                                                rules="required"
                                                type="radio"
                                                :value="answer.id"
                                                :name="`question4j${i}`"
                                            />
                                            <label :for="`input-radio-${day}-${j}`">
                                                {{ answer.text }}
                                            </label>
                                        </div>
                                        <TextInput
                                            v-if="answer.id === 4 && results[3].wkd[i].id === 4"
                                            :id="`nbRidesWkd${i}`"
                                            v-model="questions[3].alternatives[1].answers[3].nbRides[i].nb"
                                            rules="required|integer|min_value:1"
                                            :label="t('specifyNb')"
                                            :name="`nbRides${i}`"
                                            :error="errors[`nbRides${i}`]"
                                            :maxlength="2"
                                            size="small"
                                            type="number"
                                            min-value="1"
                                            max-value="99"
                                        />
                                    </li>
                                </ul>
                                <p
                                    v-show="errors[`question4j${i}`]"
                                    class="form-error"
                                    aria-live="assertive"
                                >
                                    {{ errors[`question4j${i}`] }}
                                </p>
                            </div>
                        </template>
                    </Question>
                </fieldset>
                <Question
                    v-if="results[2].id === 3"
                    :results="results"
                    :question="questions[3].alternatives[2]"
                    :current-question="3"
                >
                    <template #answers>
                        <ul class="no-bullet">
                            <li class="input-wrapper">
                                <TextInput
                                    id="nbRides"
                                    v-model="questions[3].alternatives[2].answers[0].nbRides"
                                    rules="required|integer|min_value:1"
                                    :label="t('question3.moreThan5Days.choices.text1')"
                                    name="nbRides"
                                    :error="errors['nbRides'] "
                                    :maxlength="2"
                                    size="small"
                                    :complement="t('rides')"
                                    type="number"
                                    min-value="1"
                                    max-value="99"
                                />
                            </li>
                            <li class="input-wrapper">
                                <TextInput
                                    id="nbDays5"
                                    v-model="questions[3].alternatives[2].answers[1].nbDays"
                                    rules="required|integer|min_value:1|max_value:5"
                                    :label="t('question3.moreThan5Days.choices.text2')"
                                    :maxlength="1"
                                    name="nbDays5"
                                    :error="errors['nbDays5'] "
                                    size="small"
                                    :complement="t('days')"
                                    type="number"
                                    min-value="1"
                                    max-value="5"
                                />
                            </li>
                        </ul>
                        <div class="rides-note">
                            <span class="rides-note-word">*Note: </span>
                            <p>
                                {{ t('oneRide') }}<br />
                                {{ t('twoRides') }}
                            </p>
                        </div>
                    </template>
                </Question>
                <Question
                    v-if="results[2].id === 4"
                    :results="results"
                    :question="questions[3].alternatives[2]"
                    :current-question="3"
                >
                    <template #answers>
                        <ul class="no-bullet">
                            <li class="input-wrapper">
                                <TextInput
                                    id="nbRides"
                                    v-model="questions[3].alternatives[2].answers[0].nbRides"
                                    rules="required|integer|min_value:1"
                                    :label="t('question3.moreThan5Days.choices.text1')"
                                    name="nbRides"
                                    :error="errors['nbRides'] "
                                    :maxlength="2"
                                    size="small"
                                    :complement="t('rides')"
                                    type="number"
                                    min-value="1"
                                    max-value="99"
                                />
                            </li>
                            <li class="input-wrapper">
                                <TextInput
                                    id="nbDays30"
                                    v-model="questions[3].alternatives[2].answers[1].nbDays"
                                    rules="required|integer|min_value:1|max_value:30"
                                    :label="t('question3.moreThan5Days.choices.text2')"
                                    :maxlength="2"
                                    name="nbDays30"
                                    :error="errors['nbDays30'] "
                                    size="small"
                                    :complement="t('days')"
                                    type="number"
                                    min-value="1"
                                    max-value="30"
                                />
                            </li>
                        </ul>
                        <div class="rides-note">
                            <span class="rides-note-word">*Note: </span>
                            <p>
                                {{ t('oneRide') }}<br />
                                {{ t('twoRides') }}
                            </p>
                        </div>
                    </template>
                </Question>
            </template>
            <template #[`step${additionnalQuestion?5:4}`]>
                <div
                    v-if="currentQuestion === lastStep && compiledResults && bestPrice"
                    class="results-container"
                >
                    <h3 ref="firstToRead" tabindex="0" class="summary-title">{{ t('results.title') }}</h3>
                    <div v-if="results[0].id != 1 && results[1].id != 2">
                        <ul class="no-bullet summary-list">
                            <li class="summary-item">
                                <strong class="item-label">{{ t('results.youAre') }}</strong>
                                <div class="item-content">
                                    <p>{{ compiledResults.a1.text }}</p>
                                    <div v-if="results[0].id === 3" v-html="studentMsg"></div>
                                </div>
                            </li>
                            <li class="summary-item">
                                <strong class="item-label">{{ t('results.rideHabits') }}</strong>
                                <p class="item-content">{{ compiledResults.a3.text }}</p>
                            </li>
                            <li class="summary-item">
                                <strong class="item-label">{{ t('results.nbRides') }}</strong>
                                <div class="item-content">
                                    <p v-if="results[2].id === 1">{{ compiledResults.a4.nbRides }} {{ t('rides') }}</p>
                                    <ul v-if="results[2].id === 2" class="no-bullet">
                                        <li v-for="(day, i) in questions[3].alternatives[1].days" :key="i">
                                            {{ day }} -
                                            <span v-if="results[3].wkd[i].id != 4">
                                                {{ compiledResults.a4[i].nbRides }}
                                            </span>
                                            <span v-else>{{ compiledResults.a4[i].nbRides[i].nb }}</span>
                                            {{ t('rides') }}
                                        </li>
                                    </ul>
                                    <ul v-if="results[2].id === 3 || results[2].id === 4" class="no-bullet">
                                        <li>
                                            {{ t('question3.moreThan5Days.choices.text1') }}:
                                            {{ compiledResults.a4[0].nbRides }} {{ t('rides') }}
                                        </li>
                                        <li>
                                            {{ t('question3.moreThan5Days.choices.text2Results') }}:
                                            {{ compiledResults.a4[1].nbDays }} {{ t('days') }}
                                        </li>
                                    </ul>
                                </div>
                            </li>
                        </ul>
                        <div v-if="opusCardMandatory" class="opus-card-wrapper">
                            <img class="opus-card" src="@@/img/opus-card.svg" width="45" height="36" alt="" />
                            <template v-if="results[0].id === 5">
                                {{ t('results.opusCardGeneral') }}
                            </template>
                            <template v-else>
                                {{ t('results.opusCard') }}
                            </template>
                        </div>
                        <h3 class="fares-title">{{ t('results.best_fare_title') }}</h3>
                        <ul class="no-bullet fares-list">
                            <li
                                v-for="(price, i) in bestPrice"
                                :key="i"
                                :class="[i == 0 ? 'best-fare' : 'other-option']"
                            >
                                <div
                                    v-if="i == 0"
                                    class="item-label"
                                    data-test="best-fare"
                                >
                                    <div class="best-fare-icon">
                                        <IconCheck width="15" height="12" />
                                    </div>
                                    <strong class="best-fare-title">{{ t('results.bestFare') }}</strong>
                                </div>
                                <strong v-if="i >= 1" class="other-options-title item-label">
                                    <span :class="{ visuallyhidden: i > 1 }" v-html="t('results.otherOptions')"></span>
                                </strong>
                                <div class="item-content" :class="{ 'combo-price-container': price.comboName }">
                                    <template v-if="price.comboName">
                                        <template v-if="price.comboName == 'five1dayspass'">
                                            {{ price[0].nbUnits }} {{ price[0].text }}
                                            <span v-html="formatMoney(price[0].totalPrice)"></span>
                                        </template>
                                        <template v-else>
                                            <ul v-if="price.comboName == 'wkd'" class="no-bullet">
                                                <li
                                                    v-for="(result, j) in wkdResults(price)"
                                                    :key="j"
                                                    class="flex-container align-justify"
                                                >
                                                    {{ result.nbUnits }} {{ result.text }}
                                                    <span v-html="formatMoney(result.totalPrice)"></span>
                                                </li>
                                            </ul>
                                            <ul v-if="price.comboName == 'others'" class="no-bullet">
                                                <li v-for="k in 2" :key="k" class="flex-container align-justify">
                                                    {{ price[k - 1].nbUnits }} {{ price[k - 1].text }}
                                                    <span v-html="formatMoney(price[k - 1].totalPrice)"></span>
                                                </li>
                                            </ul>
                                            <hr class="combo-total-line" />
                                            <div class="flex-container align-justify">
                                                <strong>Total: </strong>
                                                <span v-html="formatMoney(price.totalPrice)"></span>
                                            </div>
                                        </template>
                                        <div v-if="wkdTicketMsg" class="price-more-infos" v-html="pricesInfo[5].more"></div>
                                    </template>
                                    <template v-else>
                                        <span v-if="price.id !== 9">{{ price.nbUnits }}</span>
                                        {{ price.text }}
                                        <span v-html="formatMoney(price.totalPrice)"></span>
                                    </template>
                                    <div v-if="price.more" class="price-more-infos" v-html="price.more"></div>
                                </div>
                            </li>
                        </ul>
                        <div v-if="hasMonthFare" class="abonne-bus-promo">
                            <div class="abonne-bus-text">
                                <img class="promo-icon" src="@@/img/rebates.svg" width="24" height="24" alt="" />
                                <span>{{ t('results.abonneBUSMsg') }}</span>
                            </div>
                            <div class="promo-link" v-html="t('results.abonneBUSLink')"></div>
                        </div>
                        <div v-if="results[0].id === 2 || results[0].id === 6" class="family-msg">
                            <img src="@@/img/family.svg" alt="" width="42" height="48" />
                            <div v-html="familyMsg"></div>
                        </div>
                        <div v-if="transfersInfos" class="transfers-infos" v-html="transfersInfos"></div>
                    </div>
                    <div v-else>
                        <div class="summary-item">
                            <strong class="item-label">{{ t('results.youAre') }}</strong>
                            <p class="item-content">{{ compiledResults.a1.text }}</p>
                        </div>
                        <div class="best-fare">
                            <div class="best-fare-icon">
                                <IconCheck width="15" height="12" />
                            </div>
                            <div class="best-fare-title" v-html="bestPrice.text"></div>
                        </div>
                    </div>
                </div>
            </template>
        </Stepper>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Field, useForm } from 'vee-validate';

import fr from '@/i18n/fares/fr.json';
import en from '@/i18n/fares/en.json';
import Prices from '@/best-fare/prices.json';

import Stepper from '@/best-fare/components/Stepper';
import Question from '@/best-fare/components/Question';
import TextInput from '@/best-fare/components/TextInput';

import IconCheck from '@@/icons/check.svg';

const { t, locale } = useI18n({
    messages: {
        fr,
        en,
    },
});

const { errors, validate } = useForm();

const currentQuestion = ref(0);
const questions = ref([
    {
        id: 1,
        type: 'normal',
        text: t('question1a.text'),
        answers: [
            { text: t('question1a.choices.text1'), id: 1 },
            { text: t('question1a.choices.text2'), id: 6 },
            { text: t('question1a.choices.text3'), id: 2 },
            { text: t('question1a.choices.text4'), id: 3 },
            { text: t('question1a.choices.text5'), id: 4 },
            { text: t('question1a.choices.text6'), id: 5 },
        ],
    },
    {
        id: 2,
        type: 'normal',
        text: t('question1b.text'),
        answers: [
            { text: t('question1b.choices.text1'), id: 1 },
            { text: t('question1b.choices.text2'), id: 2 },
        ],
    },
    {
        id: 3,
        type: 'normal',
        text: t('question2.text'),
        answers: [
            { text: t('question2.choices.text1'), id: 1 },
            { text: t('question2.choices.text2'), id: 2 },
            { text: t('question2.choices.text3'), id: 3 },
            { text: t('question2.choices.text4'), id: 4 },
        ],
    },
    {
        id: 4,
        type: 'dynamic',
        alternatives: [
            {
                // Une journée de temps en temps
                id: 1,
                text: t('question3.oneDayAndWkd.text'),
                answers: [
                    { text: t('question3.oneDayAndWkd.choices.text1'), id: 1, nbRides: 1 },
                    { text: t('question3.oneDayAndWkd.choices.text2'), id: 2, nbRides: 2 },
                    {
                        text: t('question3.oneDayAndWkd.choices.text3'),
                        id: 3,
                        dynamicField: true,
                        nbRides: null,
                    },
                ],
            },
            {
                // La fin de semaine
                id: 2,
                text: t('question3.oneDayAndWkd.text'),
                days: [
                    t('question3.oneDayAndWkd.days.friday'),
                    t('question3.oneDayAndWkd.days.saturday'),
                    t('question3.oneDayAndWkd.days.sunday'),
                ],
                answers: [
                    { text: t('question3.oneDayAndWkd.choices.wkd0'), id: 1, nbRides: 0 },
                    { text: t('question3.oneDayAndWkd.choices.text1'), id: 2, nbRides: 1 },
                    { text: t('question3.oneDayAndWkd.choices.text2'), id: 3, nbRides: 2 },
                    {
                        text: t('question3.oneDayAndWkd.choices.text3'),
                        id: 4,
                        dynamicField: true,
                        nbRides: [],
                    },
                ],
            },
            {
                // De 3 à 5 jours et plus de 5 jours
                id: 3,
                text: t('question3.moreThan5Days.text'),
                answers: [
                    {
                        text: t('question3.moreThan5Days.choices.text1'),
                        id: 1,
                        dynamicField: true,
                        nbRides: null,
                    },
                    {
                        text: t('question3.moreThan5Days.choices.text2'),
                        id: 2,
                        dynamicField: true,
                        nbDays: null,
                    },
                ],
            },
        ],
    },
]);
const results = ref([{ id: null }, { id: null }, { id: null }, { id: null, wkd: [] }]);
const compiledResults = ref(null);
const bestPrice = ref(null);
const pricesInfo = ref([
    {
        id: 1,
        name: t('pricesInfo.month.name'),
        unlimited: true,
        nbDays: 30,
        text: t('pricesInfo.month.text'),
        more: t('pricesInfo.month.more'),
    },
    {
        id: 4,
        name: t('pricesInfo.10Trips.name'),
        unlimited: true,
        nbDays: 30,
        text: t('pricesInfo.10Trips.text'),
        more: t('pricesInfo.10Trips.more'),
    },
    {
        id: 5,
        name: t('pricesInfo.5days.name'),
        unlimited: true,
        nbDays: 5,
        text: t('pricesInfo.5days.text'),
        more: t('pricesInfo.5days.more'),
    },
    {
        id: 6,
        name: t('pricesInfo.wkd.name'),
        unlimited: true,
        nbDays: 3,
        text: t('pricesInfo.wkd.text'),
        more: t('pricesInfo.wkd.more'),
    },
    {
        id: 7,
        name: t('pricesInfo.day.name'),
        unlimited: true,
        nbDays: 1,
        text: t('pricesInfo.day.text'),
    },
    {
        id: 8,
        name: t('pricesInfo.singlePass.name'),
        unlimited: false,
        nbDays: 1,
        text: t('pricesInfo.singlePass.text'),
        more: t('pricesInfo.singlePass.more'),
    },
    {
        id: 9,
        name: t('pricesInfo.cash.name'),
        unlimited: false,
        nbDays: 1,
        text: t('pricesInfo.cash.text'),
        more: t('pricesInfo.cash.more'),
    },
    {
        id: 10,
        name: t('pricesInfo.20Tickets.name'),
        unlimited: true,
        nbDays: 30,
        text: t('pricesInfo.20Tickets.text'),
        more: t('pricesInfo.20Tickets.more'),
    },
    {
        id: 11,
        name: t('pricesInfo.eveningsAndWkd.name'),
        unlimited: true,
        nbDays: 30,
        text: t('pricesInfo.eveningsAndWkd.text'),
        more: t('pricesInfo.eveningsAndWkd.more'),
    },
]);
const prices = Prices;

const additionnalQuestion = computed(() => results.value[0].id === 6 && currentQuestion.value > 0);
const lastStep = computed(() => additionnalQuestion.value ? questions.value.length : questions.value.length - 1);
const questionsCount = computed(() => additionnalQuestion.value ? questions.value.length + 1 : questions.value.length);
const transfersInfos = computed(() => {
    const transfersOptions = bestPrice.value.find((option) => {
        return option.id === 8 || option.id === 9;
    });
    return transfersOptions ? t('results.transfers') : '';
});
const opusCardMandatory = computed(() => {
    const opusCardMandatory = bestPrice.value.find((option) => {
        return option.id === 1 || option.id === 4 || option.id === 5;
    });
    return !!opusCardMandatory;
});
const hasMonthFare = computed(() => bestPrice.value.find((price) => price.id === 1));
const studentMsg = t('results.studentException');
const familyMsg = t('results.6to18Exception');
const wkdTicketMsg = computed(() => {
    let wkdTicketMsg = [];
    wkdTicketMsg = results.value[3].wkd.find((result) => result.id === 3);
    return wkdTicketMsg && wkdTicketMsg.length !== 0;
});

function initializeData() {
    const days = questions.value[3].alternatives[1].days;
    function isWkd(el) {
        return el.wkd;
    }
    function isDynamic(el) {
        return el.dynamicField;
    }
    const answerIndex = questions.value[3].alternatives[1].answers.findIndex(isDynamic);
    const wkdIndex = results.value.findIndex(isWkd);
    days.forEach((day) => {
        questions.value[3].alternatives[1].answers[answerIndex].nbRides.push({ nb: null });
        results.value[wkdIndex].wkd.push({ id: null });
    });
}

const firstToRead = ref(null);
const stepValidationRules = computed(() => {
    if (additionnalQuestion.value) {
        return {
            0: ['question1'],
            1: ['question2'],
            2: ['question3'],
            3: getQuestion4Fields(),
        };
    } else {
        return {
            0: ['question1'],
            1: ['question3'],
            2: getQuestion4Fields(),
        };
    }
});

function getQuestion4Fields() {
    if (results.value[2].id === 1) {
        return ['question4', 'nbRides'];
    } else if (results.value[2].id === 2) {
        const nbRides = [];
        const question4j = [];
        results.value[3].wkd.forEach((wkdResult, i) => {
            question4j.push(`question4j${i}`);
            if (wkdResult && wkdResult.id === 4) {
                nbRides.push(`nbRides${i}`);
            }
        });
        return [...question4j, ...nbRides];
    } else if (results.value[2].id === 3) {
        return ['nbRides', 'nbDays5'];
    } else if (results.value[2].id === 4) {
        return ['nbRides', 'nbDays30'];
    }
    return [];
}

async function next() {
    const fieldsToValidate = stepValidationRules.value[currentQuestion.value] || [];
    if (fieldsToValidate.length > 0) {
        await validate({
            names: fieldsToValidate
        });
        const currentStepHasErrors = fieldsToValidate.some(fieldName => errors.value[fieldName]);
        if (currentStepHasErrors) {
            fieldsToValidate.filter(fieldName => errors.value[fieldName]);
            return;
        }
    }
    currentQuestion.value++;
    if (
        currentQuestion.value === lastStep.value ||
        results.value[0].id === 1 ||
        (results.value[1].id === 2 && currentQuestion.value > 1)
    ) {
        await nextTick();
        // firstToRead.value.focus();
        currentQuestion.value = lastStep.value;
        compileResults();
    }
}

function prev() {
    currentQuestion.value--;
    if (results.value[0].id === 1) {
        currentQuestion.value = 0;
    }
    if (results.value[1].id === 2 && currentQuestion.value !== 0) {
        currentQuestion.value = 1;
    }
}

function restart() {
    currentQuestion.value = 0;
    compiledResults.value = null;
    bestPrice.value = null;
    results.value = [{ id: null }, { id: null }, { id: null }, { id: null, wkd: [] }];
    questions.value[3].alternatives[0].answers[2].nbRides = null;
    questions.value[3].alternatives[1].answers[3].nbRides = [];
    questions.value[3].alternatives[2].answers[0].nbRides = null;
    questions.value[3].alternatives[2].answers[1].nbDays = null;
    initializeData();
    window.scrollTo(0, 0);
}

function compileResults() {
    compiledResults.value = {};
    const a1 = questions.value[0].answers.find((el) => {
        return el.id === results.value[0].id;
    });
    compiledResults.value.a1 = a1;
    if (results.value[0].id !== 1 || results.value[1].id !== 2) {
        const a3 = questions.value[2].answers.find((el) => {
            return el.id === results.value[2].id;
        });
        compiledResults.value.a3 = a3;
        let a4 = null;
        if (results.value[2].id === 1) {
            a4 = questions.value[3].alternatives[0].answers.find((el) => {
                return el.id === results.value[3].id;
            });
        } else if (results.value[2].id === 2) {
            a4 = [];
            results.value[3].wkd.forEach((day) => {
                a4.push(
                    questions.value[3].alternatives[1].answers.find((el) => {
                        return el.id === day.id;
                    }),
                );
            });
        } else {
            a4 = questions.value[3].alternatives[2].answers;
        }
        compiledResults.value.a4 = a4;
    }
    findBestPrice();
}

function findBestPrice() {
    const customerType = prices.find((el) => {
        return el.customerType === results.value[0].id;
    });
    if (customerType.customerType !== 1) {
        customerType.availablePrices.forEach((price) => {
            const infos = pricesInfo.value.find((infos) => {
                return infos.id === price.id;
            });
            price.name = infos.name;
            price.nbDays = infos.nbDays;
            price.text = infos.text;
            price.unlimited = infos.unlimited;
            price.more = infos.more || '';
        });
    }
    let possibilities = [];
    if (customerType.customerType === 1) {
        // Exception pour enfant
        possibilities = [{ text: null }];
        possibilities.text = t('results.childException');
    } else if (customerType.customerType === 6 && results.value[1].id === 2) {
        // Exception pour personne de 6 à 12 ans accompagnée d'un adulte
        possibilities = [{ text: null }];
        possibilities.text = t('results.teenException');
    } else if (results.value[2].id === 1) {
        possibilities = pricesOneDay(customerType);
    } else if (results.value[2].id === 2) {
        possibilities = pricesWkd(customerType);
    } else {
        possibilities = prices3to5OrMore(customerType);
    }
    let nbRides;
    if (results.value[2].id === 1) {
        nbRides = parseInt(compiledResults.value.a4.nbRides);
    } else if (
        (customerType.customerType === 3 || customerType.customerType === 5) &&
        results.value[2].id === 2
    ) {
        nbRides = compiledResults.value.a4.reduce((a, c, i) => {
            if (Array.isArray(c.nbRides)) {
                return a + parseInt(c.nbRides[i].nb);
            }
            return a + parseInt(c.nbRides);
        }, 0);
    } else {
        nbRides = parseInt(compiledResults.value.a4[0].nbRides);
    }
    // Exception pour l'option pour aînés pour 10 passages (non valide la fin de semaine)
    if (results.value[2].id !== 2 && customerType.customerType === 4 && nbRides >= 10) {
        const seniorPrice = customerType.availablePrices.find((price) => price.id === 4);
        seniorPrice.nbUnits = Math.ceil(nbRides / 10);
        seniorPrice.totalPrice = seniorPrice.nbUnits * seniorPrice.unitPrice;
        possibilities.push(seniorPrice);
    }
    // Exception pour la catégorie générale pour 20 billets économiques
    if ((customerType.customerType === 3 || customerType.customerType === 5) && nbRides === 20) {
        const generalPrice = customerType.availablePrices.find((price) => price.id === 10);
        generalPrice.totalPrice = generalPrice.unitPrice;
        possibilities.push(generalPrice);
    }
    // Tri des possibilités par prix ascendant
    possibilities.sort((p1, p2) => {
        return p1.totalPrice - p2.totalPrice;
    });
    bestPrice.value = possibilities;
}

// Détermine le prix total et le nombre d'unités du tarif proposé
function totalPriceAndUnits(price, nbRides = 1, totalNbDays = 1) {
    if (price.unlimited) {
        price.nbUnits = Math.ceil(totalNbDays / price.nbDays);
    } else {
        price.nbUnits = nbRides;
    }
    price.totalPrice = price.unitPrice * price.nbUnits;
    return price;
}

// Méthodes de calcul selon les habitudes de déplacement
// Rassemble tous les tarifs disponibles pour une journée
function pricesOneDay(customerType) {
    const possibilities = [];
    const nbRides = compiledResults.value.a4.nbRides;
    customerType.availablePrices.forEach((price) => {
        if (price.nbDays === 1) {
            price = totalPriceAndUnits(price, nbRides);
            possibilities.push(price);
        }
    });
    return possibilities;
}

// Rassemble tous les tarifs disponibles pour la fin de semaine
function pricesWkd(customerType) {
    const possibilities = [];
    const nbRidesTotal = compiledResults.value.a4.reduce((a, c, i) => {
        if (Array.isArray(c.nbRides)) {
            return a + parseInt(c.nbRides[i].nb);
        }
        return a + parseInt(c.nbRides);
    }, 0);
    // Traitement des tarifs englobants toute la fin de semaine
    customerType.availablePrices.forEach((price, i) => {
        if (price.id === 8 || price.id === 9 || price.id === 6 || price.id === 11) {
            price = totalPriceAndUnits(price, nbRidesTotal, questions.value[3].alternatives[1].days.length);
            possibilities.push(price);
        }
    });
    // Traitement du combo billets/passe de jour indépendamment de chaque jour
    const combo = { totalPrice: 0 };
    const priceOneDay = customerType.availablePrices.find((el) => {
        return el.id === 7;
    });
    const priceTickets = customerType.availablePrices.find((el) => {
        return el.id === 8;
    });
    questions.value[3].alternatives[1].days.forEach((day, i) => {
        combo[day] = null;
        combo.comboName = 'wkd';
        const nbRides =
            typeof compiledResults.value.a4[i].nbRides === 'number'
                ? compiledResults.value.a4[i].nbRides
                : compiledResults.value.a4[i].nbRides[i].nb;
        const tarifCombo =
            priceTickets.unitPrice * nbRides > priceOneDay.unitPrice ? priceOneDay : priceTickets;
        combo[day] = totalPriceAndUnits(Object.assign({}, tarifCombo), nbRides);
        combo.totalPrice += combo[day].totalPrice;
    });
    if (combo.totalPrice !== priceTickets.totalPrice) {
        possibilities.push(combo);
    }
    return possibilities;
}

// Rassemble tous les tarifs disponibles pour 3 à 5 jours ou plus
function prices3to5OrMore(customerType) {
    const possibilities = [];
    const nbRides = compiledResults.value.a4[0].nbRides;
    const nbDays = compiledResults.value.a4[1].nbDays;
    const priceMonth = customerType.availablePrices.find((el) => {
        return el.id === 1;
    });
    const price5Days = customerType.availablePrices.find((el) => {
        return el.id === 5;
    });
    switch (true) {
        case nbDays < 5:
            customerType.availablePrices.forEach((price, i) => {
                if (price.nbDays <= 5 && price.nbDays !== 3) {
                    totalPriceAndUnits(price, nbRides, nbDays);
                    possibilities.push(price);
                }
            });
            break;
        case nbDays >= 5 && nbDays <= 10: {
            const combo = { totalPrice: 0 };
            customerType.availablePrices.forEach((price, i) => {
                if (price.id === 8 || price.id === 9 || price.id === 5 || price.id === 7) {
                    totalPriceAndUnits(price, nbRides, nbDays);
                    if (
                        ((price.id === 8 || price.id === 9) && price.totalPrice < priceMonth.unitPrice) ||
                        (price.id === 7 && price.totalPrice < price5Days.unitPrice) ||
                        price.id === 5
                    ) {
                        possibilities.push(price);
                    }
                    if (nbDays === '5' && price.id === 7) {
                        combo.comboName = 'five1dayspass';
                        combo[0] = totalPriceAndUnits(Object.assign({}, price), nbRides, 5);
                        combo.totalPrice += combo[0].totalPrice;
                        possibilities.push(combo);
                    }
                    if (nbDays !== 5 && price.id === 1) {
                        possibilities.push(price);
                    }
                    if (nbDays > 5 && nbDays < 10) {
                        combo.comboName = 'others';
                        if (price.id === 5) {
                            combo[0] = totalPriceAndUnits(Object.assign({}, price), nbRides, 5);
                            combo.totalPrice += combo[0].totalPrice;
                            possibilities.push(combo);
                        } else if (price.id === 7) {
                            combo[1] = totalPriceAndUnits(Object.assign({}, price), nbRides, nbDays - 5);
                            combo.totalPrice += combo[1].totalPrice;
                        }
                    }
                }
            });
            break;
        }
        case nbDays > 10:
            customerType.availablePrices.forEach((price, i) => {
                totalPriceAndUnits(price, nbRides, nbDays);
                if (
                    ((price.id === 8 || price.id === 9) && price.totalPrice < priceMonth.unitPrice) ||
                    price.id === 1
                ) {
                    possibilities.push(price);
                }
            });
            break;
    }
    return possibilities;
}

function wkdResults(results) {
    const wkdResults = [];
    const days = questions.value[3].alternatives[1].days;
    for (let i = 0; i < days.length; i++) {
        const currentResultInfos = results[days[i]];
        const resultAlreadyHere = wkdResults.find((r) => {
            return r.id === currentResultInfos.id;
        });
        if (!resultAlreadyHere) {
            wkdResults.push({ ...currentResultInfos });
        } else {
            const index = wkdResults.findIndex((r) => {
                return r.id === currentResultInfos.id;
            });
            wkdResults[index].nbUnits += currentResultInfos.nbUnits;
            wkdResults[index].totalPrice += currentResultInfos.totalPrice;
        }
    }
    return wkdResults;
}

function formatMoney(price) {
    const frFormat = `${price.toFixed(2).replace('.', ',')}&nbsp;$`;
    const enFormat = `$${price.toFixed(2).replace(',', '.')}`;
    return locale.value === 'fr' ? frFormat : enFormat;
}

onMounted(() => initializeData());
</script>

<style lang="scss" scoped>
.quiz-container {
    padding: 24px 16px 32px;
    background-color: $color-white;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 40px;
    }
}
.family-msg {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin: 24px 0;
}
.transfers-infos {
    color: $color-black-50;
}
.rides-note,
.price-more-infos {
    @include rem-typo(14px);
    color: $color-black-50;
    margin-top: 16px;
    @include breakpoint(medium) {
        @include rem-typo(16px);
    }
}
.rides-note {
    display: flex;
    align-items: baseline;
}
.rides-note-word {
    margin-right: 8px;
}
.opus-card-wrapper {
    display: flex;
    align-items: center;
    margin-top: 16px;
}
.opus-card {
    margin-top: -4px;
    margin-right: 8px;
}
.abonne-bus-promo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-top: 16px;
    .abonne-bus-text {
        display: flex;
        align-items: flex-start;
        margin-right: 16px;
        @include breakpoint(medium) {
            margin-right: 32px;
        }
    }
    .promo-icon {
        margin-right: 8px;
    }
    :deep(.button) {
        background-color: $color-green-50;
        &:hover,
        &:focus-visible {
            background-color: $color-green-70;
        }
    }
}
.day-label {
    @include rem-typo(16px);
}
.results-container {
    margin-bottom: 32px;
}
.summary-title,
.fares-title {
    @include rem-typo(18px);
    font-weight: 800;
    @include breakpoint(medium) {
        @include rem-typo(20px);
    }
}
.fares-title {
    margin-top: 24px;
}
.summary-item {
    display: flex;
    flex-direction: column;
    gap: 8px 16px;
    margin-bottom: 8px;
    background-color: $color-black-2;
    @include breakpoint(medium) {
        align-items: flex-start;
        flex-direction: row;
    }
    &:last-child {
        margin-bottom: 0;
    }
}
.item-label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    @include breakpoint(medium) {
        width: 25%;
    }
}
.item-content {
    flex-grow: 1;
    margin: 0;
}
.other-option,
.best-fare,
.summary-item {
    padding: 16px;
    border-radius: 15px;
    @include breakpoint(medium) {
        padding: 24px;
    }
}
.other-option {
    display: flex;
    flex-direction: column;
    gap: 8px 16px;
    border-bottom: 2px solid $color-black-2;
    border-radius: 0;
    @include breakpoint(medium) {
        align-items: flex-start;
        flex-direction: row;
    }
    &:last-child {
        border: none;
    }
    .item-content {
        @include breakpoint(medium) {
            width: 75%;
            margin-left: auto;
        }
    }
}
.best-fare {
    display: flex;
    flex-direction: column;
    gap: 8px 16px;
    color: $color-green-80;
    background-color: $color-green-2;
    @include breakpoint(medium) {
        align-items: flex-start;
        flex-direction: row;
    }
}
.best-fare-title,
.other-options-title {
    @include breakpoint(medium) {
        @include rem-typo(18px);
    }
}
.best-fare-title {
    flex-grow: 1;
}
.other-options-title {
    @include breakpoint(medium) {
        padding-left: 32px;
    }
}
.combo-price-container {
    max-width: 300px;
}
.combo-total-line {
    margin: 0.5rem 0;
}
.best-fare-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: $color-green-40;
    svg {
        width: 15px;
        height: 12px;
        fill: $color-white;
    }
}
.input-wrapper {
    margin-bottom: 16px;
}
.radio-list {
    margin-bottom: 0;
}
.radio-list-wrapper {
    margin-bottom: 24px;
}
</style>
